import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  FormControlLabel,
  Grid2 as Grid,
  IconButton as MuiIconButton,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { PhoneField, SelectField } from "src/shared/components";
import { regexps, validatePhone } from "src/shared/utils/hookFormRules";
import { messages } from "src/shared/utils/hookFormRules/messages";
import { IPartnerFormValues } from "../../model/types";
interface IContactItemProps {
  remove?: () => void;
  index: number;
}
export const ContactItem: FC<IContactItemProps> = ({ index, remove }) => {
  const { data: dropdowns } = useGetPartnerDropdownsQuery();
  const { partner_person_sex, partner_person_position } = dropdowns || {};
  const { control } = useFormContext<IPartnerFormValues>();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        gap: 4,
        pb: 4,
      }}
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.lastname`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                inputRef={ref}
                fullWidth
                {...field}
                label="Фамилия"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.firstname`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                inputRef={ref}
                fullWidth
                {...field}
                label="Имя"
                error={!!error}
                helperText={error?.message}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2, lg: 1 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.sex`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField
                inputRef={ref}
                fullWidth
                {...field}
                label="Пол"
                error={!!error}
                helperText={error?.message}
                options={partner_person_sex}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.position`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField
                inputRef={ref}
                fullWidth
                {...field}
                label="Должность"
                error={!!error}
                helperText={error?.message}
                options={partner_person_position}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.email`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                inputRef={ref}
                fullWidth
                {...field}
                label="E-mail"
                error={!!error}
                helperText={error?.message}
              />
            )}
            rules={{
              validate(fieldValue, formValues) {
                if (
                  !formValues["contact_persons"][index].phone_main &&
                  !fieldValue
                ) {
                  return messages.phoneOrEmail;
                }
              },
              pattern: {
                value: regexps.emailRegexp,
                message: messages.email,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.phone_main`}
            render={({
              field: { ref, onChange, ...field },
              fieldState: { error },
            }) => (
              <PhoneField
                {...field}
                inputRef={ref}
                label="Основной телефон"
                error={!!error}
                helperText={error?.message}
                onChange={(_fieldValue, info) => {
                  onChange(info);
                }}
              />
            )}
            rules={{
              validate(fieldValue, formValues) {
                if (
                  !formValues["contact_persons"][index].email &&
                  !fieldValue
                ) {
                  return messages.phoneOrEmail;
                }

                return validatePhone(fieldValue);
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.phone_additional`}
            render={({
              field: { ref, onChange, ...field },
              fieldState: { error },
            }) => (
              <PhoneField
                inputRef={ref}
                onChange={(_fieldValue, info) => {
                  onChange(info);
                }}
                {...field}
                label="Дополнительный телефон"
                error={!!error}
                helperText={error?.message}
              />
            )}
            rules={{
              validate: (fieldValue) => validatePhone(fieldValue),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name={`contact_persons.${index}.messengers.${0}.login`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                inputRef={ref}
                fullWidth
                {...field}
                label="Skype"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 4, sm: 3, lg: 2 }}>
          <Controller
            name={`contact_persons.${index}.feedback_call_consent`}
            render={({ field }) => (
              <FormControlLabel
                sx={{ height: "100%" }}
                control={<Switch {...field} checked={!!field.value} />}
                label="QA обзвон"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 4, sm: 3, lg: 2 }}>
          <Controller
            name={`contact_persons.${index}.is_decision_maker`}
            render={({ field }) => (
              <FormControlLabel
                sx={{ height: "100%" }}
                control={<Switch {...field} checked={!!field.value} />}
                label="ЛПР"
              />
            )}
          />
        </Grid>
      </Grid>
      <Stack
        direction={"row"}
        spacing={1}
        sx={{
          minWidth: 72,
        }}
      >
        {!!remove && (
          <MuiIconButton
            size="small"
            color="inherit"
            sx={{ minWidth: 0, color: "grey.600" }}
            onClick={remove}
          >
            <DeleteOutlineIcon color="inherit" />
          </MuiIconButton>
        )}
      </Stack>
    </Box>
  );
};
